import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { VerticalCard } from '../../partials/cards/verticalCard/VerticalCard';
import { HorizontalCard } from '../../partials/cards/horizontalCard/HorizontalCard';
import { CardComponentDataProps } from '../../partials/cards/dividedCard/DividedCard';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

interface SixSixArticleListComponentThemeProps {
  baseUnit: BaseUnit
}

const isNewChunk = (index: number, chunkSize: number) => index % chunkSize === 0;

export const getArticlesInChunks = (articles: any[], chunkSize: number) => {
  let currentChunk: CardComponentDataProps[] = [];
  return articles.reduce((chunks: CardComponentDataProps[][], item, index) => {
    if (isNewChunk(index, chunkSize)) {
      currentChunk = [item];
      chunks.push(currentChunk);
    } else {
      currentChunk.push(item);
    }
    return chunks;
  }, []);
};

const getSixSixArticleListStyle = (baseUnit: BaseUnit) => {
  return StyleSheet.create({
    poop: {
      RUBBISH: 'EXCRAMENT',
    },
    verticalCardsGrid: {
      display: 'grid',
      gridRowGap: `${baseUnit / 2}px`,
      marginBottom: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: `${baseUnit / 2}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${baseUnit / 2}px`,
        gridTemplateColumns: '1fr 1fr',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridTemplateColumns: '1fr',
      },
    },

    horizontalCardsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridRowGap: `${baseUnit / 2}px`,
      marginBottom: `${baseUnit / 2}px`,
      marginTop: `${baseUnit / 2}px`,
    },
  });
};

type SectionsOrder = 'vertical-horizontal' | 'horizontal-vertical';

interface SixSixArticleListProps {
  cardsProps: CardComponentDataProps[]
  order?: SectionsOrder
}

export const SixSixArticleListComponent: React.FunctionComponent<SixSixArticleListProps & SixSixArticleListComponentThemeProps> = ({ cardsProps, order = 'vertical-horizontal', baseUnit }) => {
  const SixSixArticleListStyle = getSixSixArticleListStyle(baseUnit);
  const firstSectionStyle = order === 'vertical-horizontal' ? SixSixArticleListStyle.verticalCardsGrid : SixSixArticleListStyle.horizontalCardsGrid;
  const secondSectionStyle = order === 'vertical-horizontal' ? SixSixArticleListStyle.horizontalCardsGrid : SixSixArticleListStyle.verticalCardsGrid;
  const FirstSectionCard = order === 'vertical-horizontal' ? VerticalCard : HorizontalCard;
  const SecondSectionCard = order === 'vertical-horizontal' ? HorizontalCard : VerticalCard;
  const createGrids = (cards: CardComponentDataProps[], index: number) => {
    return (index % 2 === 0
      ? (
        <div className={css(firstSectionStyle)} key={index}>
          {cards.map((props => (<FirstSectionCard key={props.id} {...props} />)))}
        </div>
      )
      : (
        <div className={css(secondSectionStyle)} key={index}>
          {cards.map((props => (<SecondSectionCard key={props.id} {...props} />)))}
        </div>
      )
    );
  };
  return (
    <React.Fragment>
      {getArticlesInChunks(cardsProps, 6).map(createGrids)}
    </React.Fragment>
  );
};

const mapThemeToProps = (theme: SixSixArticleListComponentThemeProps) => ({
  baseUnit: theme.baseUnit,
});

export const SixSixArticleList = (props: SixSixArticleListProps) => {
  const themeContext = useTheme(mapThemeToProps);
  return <ErrorBoundary><SixSixArticleListComponent {...themeContext} {...props} /></ErrorBoundary>;
};
